import type { AxiosError, AxiosInstance } from 'axios';
import type { NotificationConfig } from './notifications';
import type {
  CustomerPortalResponse,
  NewSubscriptionResponse,
  SubscriptionPlanConfig,
  SubscriptionResponse,
  UsageResponse,
} from './types';
import { makeAuthConfig, makeAuthHeaders } from './utils/make-auth-headers';

export interface RustySubscriptionApiProps {
  client: AxiosInstance;
}

export interface RustySubscriptionSpec {
  getSubscriptionPlans(): Promise<SubscriptionPlanConfig[]>;
  getCurrentSubscription(
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<SubscriptionResponse | null>;
  subscribeToplan(
    planId: string,
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<NewSubscriptionResponse>;
  cancelSubscription(accessToken: string): Promise<unknown>;
  getSubscriptionUsage(
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<UsageResponse[]>;
  createCustomerPortalSession(
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<CustomerPortalResponse>;
}

export class RustySubscription implements RustySubscriptionSpec {
  private client: AxiosInstance;

  constructor({ client }: RustySubscriptionApiProps) {
    this.client = client;
  }

  async getSubscriptionPlans(): Promise<SubscriptionPlanConfig[]> {
    const result = await this.client.get<SubscriptionPlanConfig[]>('/subscriptions/plans');
    return result.data;
  }

  async getCurrentSubscription(
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<SubscriptionResponse | null> {
    try {
      const result = await this.client.get<SubscriptionResponse>(
        '/a/subscriptions/current',
        makeAuthConfig(accessToken, notification)
      );
      return result.data;
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        return null; // No active subscription
      }
      throw error;
    }
  }

  async subscribeToplan(
    planId: string,
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<NewSubscriptionResponse> {
    const result = await this.client.post(
      `/a/subscriptions/${planId}`,
      {},
      makeAuthConfig(accessToken, notification)
    );
    return result.data;
  }

  async cancelSubscription(accessToken: string): Promise<unknown> {
    const result = await this.client.delete('/a/subscriptions', {
      headers: makeAuthHeaders(accessToken),
    });
    return result.data;
  }

  async getSubscriptionUsage(
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<UsageResponse[]> {
    const result = await this.client.get<UsageResponse[]>(
      '/a/subscriptions/usage',
      makeAuthConfig(accessToken, notification)
    );
    return result.data;
  }

  async createCustomerPortalSession(
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<CustomerPortalResponse> {
    const result = await this.client.post<CustomerPortalResponse>(
      '/a/subscriptions/portal',
      {},
      makeAuthConfig(accessToken, notification)
    );
    return result.data;
  }
}
